import { GridDimension } from "client/Api/Workspace/common";
import {
	Anchor,
	DropOverMenu,
	Form,
	FormItem,
	Icon,
	Select,
	SelectItem,
	Toolbar
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import { Coordinate } from "client/Misc/commonTypes";
import { isNil } from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import {
	customColSpanValues,
	customRowSpanValues,
	minCustomColSpan,
	minCustomRowSpan
} from "client/Features/Workspace/appTypeHelper";
import {
	ReactElement,
	useMemo,
	useState
} from "react";
import styles from "./CustomDimensionMenu.module.less";

export type CustomDimensionMenuProps = {
	associatedElement: HTMLElement;
	position: Coordinate;
	initialDimension: GridDimension;
	onBack: () => void;
	onSet: (dimension: GridDimension) => void;
	onCancel: () => void;
	trackingId: string;
};

export const CustomDimensionMenu = (props: CustomDimensionMenuProps): ReactElement => {
	const {
		associatedElement,
		position,
		initialDimension,
		onBack,
		onSet,
		onCancel,
		trackingId
	} = props;

	const [formData, setFormData] = useState<FormData>(
		() => {
			const { columnSpan, rowSpan } = initialDimension;

			return {
				columnSpan: columnSpan >= minCustomColSpan
					? columnSpan
					: undefined,
				rowSpan: rowSpan >= minCustomRowSpan
					? rowSpan
					: undefined,
			};
		}
	);

	const formHandler: IFormHandler<FormData> = useFormHandler(
		formData,
		setFormData,
		"NoValidation"
	);

	const saveHandler = useMemo(
		(): (() => void) | undefined => {
			const { columnSpan, rowSpan } = formData;
			if (isNil(columnSpan) || isNil(rowSpan)) {
				return undefined;
			}

			return (): void => onSet({ columnSpan, rowSpan});
		},
		[formData, onSet]
	);

	return (
		<DropOverMenu
			trackingId={trackingId}
			title={localize("_CustomSize")}
			target={associatedElement}
			targetAlignment={{ kind: "absolute", coordinate: position }}
			onClose={onCancel}
		>
			<Form onSubmit={saveHandler ?? "NONE"}>
				<div className={styles.root}>
					<h4>
						<Anchor onClick={() => onBack()} className={styles.header}>
							<Icon
								value="chevron-left"
							/>
							{localize("_CustomSize")}
						</Anchor>
					</h4>
					<FormItem label={localize("_Width")}>
						<Select
							{...formHandler.for("columnSpan").attachToSelect()}
							options={colSpanOptions}
							menuTitle={localize("_Width")}
						/>
					</FormItem>
					<FormItem label={localize("_Height")}>
						<Select
							{...formHandler.for("rowSpan").attachToSelect()}
							options={rowSpanOptions}
							menuTitle={localize("_Height")}
						/>
					</FormItem>
					<div>
						<Toolbar>
							<Toolbar.Button
								kind="primary"
								isDisabled={!saveHandler}
								onClick={saveHandler}
								text={localize("_Set")}
								trackingId="set"
							/>

							<Toolbar.Button
								kind="secondary"
								text={localize("_Cancel")}
								onClick={onCancel}
								trackingId="cancel"
							/>
						</Toolbar>
					</div>
				</div>
			</Form>
		</DropOverMenu>
	);
};
CustomDimensionMenu.displayName = "CustomDimensionMenu";

type FormData = {
	columnSpan: number | undefined;
	rowSpan: number | undefined;
};

const colSpanOptions: readonly SelectItem<number>[] = customColSpanValues
	.map((x): SelectItem<number> => ({
		text: `${x}`,
		value: x
	}));

const rowSpanOptions: readonly SelectItem<number>[] = customRowSpanValues
	.map((x): SelectItem<number> => ({
		text: `${x}`,
		value: x
	}));